import { extend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import Icon from 'flarum/common/components/Icon';
import LinkButton from 'flarum/common/components/LinkButton';

// unreadPrivateMessagesCount is registered globally on the User model by
// fof/byobu's own extend.ts (new Extend.Model(User).attribute<number>(...)),
// not this extension - so this only works while that extension is active,
// same implicit (schema-level, not composer-level) coupling as e.g.
// champis-species' own authorDisplayName.ts uses for champisDisplayName.

// byobuPrivate ('/private') is registered by fof/byobu's own forum/extend.ts
// (new Extend.Routes().add('byobuPrivate', '/private', IndexPage)) - not
// part of core's own ForumRoutes interface unless declared here too, same
// as champis-species' own speciesTypes.ts does for its route helpers.

/**
 * Restores the always-visible header icon flarum/messages used to have
 * (HeaderSecondary, priority 15 - the exact slot and position it used) for
 * private messages - fof/byobu itself only adds a link inside the
 * discussions-index sidebar (IndexSidebar), which is invisible from every
 * other page (a species page, a discussion, /admin, ...). Requested
 * directly after switching from flarum/messages to fof/byobu: "Regarde pour
 * les messages privés pour que ce soit facilement accessible."
 *
 * A plain LinkButton with a custom getButtonContent() override, not a real
 * HeaderDropdown (unlike flarum/messages' own DialogsDropdown): byobu ships
 * no compact "recent conversations" list component of its own to open in a
 * dropdown menu, and building one from scratch is a much bigger feature
 * than "restore the shortcut" - this just navigates straight to /private,
 * the same way the sidebar link already does. The icon+unread-badge markup
 * below is copied from flarum/core's own HeaderDropdown.getButtonContent()
 * (icon, then Bubble, then label, as siblings - not the badge nested inside
 * .Button-label the way a plain Button would render it) specifically so it
 * inherits that component's existing .Bubble/.HeaderDropdownBubble styling
 * for free; ChampisPrivateMessagesLink's own forum.less rule below gives it
 * the position:relative anchor HeaderDropdown otherwise gets from wrapping
 * everything in a real Dropdown (which itself sets position:relative).
 */
// Icon-only in the desktop header - explicitly requested: "dans le menu,
// peux-tu juste laisser l'icône et retirer 'Messages privés'" - but with
// its label in the phone's drawer menu, among items that all have one
// ("Discussions privées", requested 2026-09-27): the same as core's own
// NotificationsDropdown, whose label only the tablet-up header hides (see
// champis-theme's .ChampisPrivateMessagesLink). aria-label keeps it
// named for screen readers where the label is hidden.
class PrivateMessagesLinkButton extends LinkButton {
  getButtonContent() {
    const unread = app.session.user?.unreadPrivateMessagesCount() || 0;
    return [m(Icon, {
      name: "fas fa-envelope",
      className: "Button-icon"
    }), unread > 0 && m("span", {
      className: "Bubble HeaderDropdownBubble",
      "data-digits": String(unread).length
    }, unread), m("span", {
      className: "Button-label"
    }, this.attrs['aria-label'])];
  }
}
export default function addPrivateMessagesHeaderLink() {
  extend(HeaderSecondary.prototype, 'items', function (items) {
    if (!app.session.user) return;
    items.add('champisPrivateMessages', m(PrivateMessagesLinkButton, {
      className: "Button Button--flat ChampisPrivateMessagesLink",
      href: app.route('byobuPrivate'),
      "aria-label": app.translator.trans('champis-others.forum.private_messages_link.label')
    }), 15);
  });
}